<template>
  <node-view-wrapper as="div" class="price-enroll-block not-prose" contenteditable="false">
    <block-controls :editor="editor" :get-pos="getPos" :delete-node="deleteNode" :label="$t('products.block_price_enroll')" />

    <!-- Preview: same inline styles as the saved HTML (see priceEnrollStyles.js) -->
    <div :style="styles.wrapper">
      <h2 v-if="node.attrs.price" :style="styles.heading"><strong :style="priceStyle">{{ node.attrs.price }}</strong></h2>
      <div v-if="validUrl" :style="styles.buttonRow">
        <a :href="validUrl" target="_blank" rel="noopener noreferrer" :style="buttonStyle" @click.prevent>{{ node.attrs.buttonText }}</a>
      </div>
      <p v-else role="status" class="price-enroll-warning">{{ $t('products.price_enroll_no_button') }}</p>
      <p v-if="node.attrs.note" :style="styles.note">{{ node.attrs.note }}</p>
    </div>

    <!-- Form -->
    <div class="grid grid-cols-1 gap-3 md:grid-cols-2 mt-3">
      <div>
        <label :for="fieldId('price')" class="price-enroll-label">{{ $t('products.price_enroll_price') }}</label>
        <input :id="fieldId('price')" type="text" :value="node.attrs.price" class="price-enroll-input"
               :placeholder="$t('products.price_enroll_price_placeholder')"
               @input="update('price', $event.target.value)" />
      </div>
      <div>
        <label :for="fieldId('button-text')" class="price-enroll-label">{{ $t('products.price_enroll_button_text') }}</label>
        <input :id="fieldId('button-text')" type="text" :value="node.attrs.buttonText" class="price-enroll-input"
               @input="update('buttonText', $event.target.value)" />
      </div>
      <div class="md:col-span-2">
        <label :for="fieldId('button-url')" class="price-enroll-label">{{ $t('products.price_enroll_button_url') }}</label>
        <input :id="fieldId('button-url')" type="url" :value="urlDraft" class="price-enroll-input"
               placeholder="https://forms.gle/..."
               :aria-invalid="urlError ? 'true' : 'false'"
               :aria-describedby="urlError ? fieldId('button-url-error') : null"
               @input="onUrlInput($event.target.value)" @change="commitUrl" />
        <p v-if="urlError" :id="fieldId('button-url-error')" class="mt-1 text-xs text-red-700">
          {{ $t('products.price_enroll_url_invalid') }}
        </p>
      </div>
      <div>
        <label :for="fieldId('note')" class="price-enroll-label">{{ $t('products.price_enroll_note') }}</label>
        <input :id="fieldId('note')" type="text" :value="node.attrs.note" class="price-enroll-input"
               @input="update('note', $event.target.value)" />
      </div>
      <div>
        <label :for="fieldId('button-color')" class="price-enroll-label">{{ $t('products.price_enroll_button_color') }}</label>
        <input :id="fieldId('button-color')" type="color" :value="buttonColor"
               class="mt-1 h-9 w-16 cursor-pointer rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-bpblue"
               @input="update('buttonColor', normalizeHexColor($event.target.value) || defaultButtonColor)" />
      </div>
    </div>
  </node-view-wrapper>
</template>

<script>
import { NodeViewWrapper, nodeViewProps } from '@tiptap/vue-3'
import BlockControls from './BlockControls.vue'
import { normalizeHexColor } from './colorAttributes'
import { safeLinkUrl } from './linkAttributes'
import { DEFAULT_BUTTON_COLOR, PRICE_ENROLL_STYLES, priceEnrollButtonStyle, priceEnrollPriceStyle } from './priceEnrollStyles'

export default {
  name: 'PriceEnrollNodeView',

  components: {
    NodeViewWrapper,
    BlockControls,
  },

  props: nodeViewProps,

  data() {
    return {
      // The link is only stored once it's valid; meanwhile the field keeps what's typed.
      urlDraft: this.node.attrs.buttonUrl,
      urlError: false,
      styles: PRICE_ENROLL_STYLES,
      defaultButtonColor: DEFAULT_BUTTON_COLOR,
    }
  },

  computed: {
    buttonColor() {
      return normalizeHexColor(this.node.attrs.buttonColor) || DEFAULT_BUTTON_COLOR
    },
    buttonStyle() {
      return priceEnrollButtonStyle(this.buttonColor)
    },
    priceStyle() {
      return priceEnrollPriceStyle(this.node.attrs.price)
    },
    validUrl() {
      return safeLinkUrl(this.node.attrs.buttonUrl)
    },
  },

  watch: {
    // Keep the field in sync when the link changes from outside (undo/redo).
    'node.attrs.buttonUrl'(value) {
      this.urlDraft = value
      this.urlError = false
    },
  },

  methods: {
    normalizeHexColor,
    fieldId(name) {
      return `price-enroll-${this.$.uid}-${name}`
    },
    update(name, value) {
      this.updateAttributes({ [name]: value })
    },
    onUrlInput(value) {
      this.urlDraft = value
      this.urlError = false
    },
    commitUrl() {
      const value = this.urlDraft.trim()
      const url = safeLinkUrl(value)
      if (value && !url) {
        this.urlError = true
        return
      }
      this.update('buttonUrl', url || '')
    },
  },
}
</script>

<style scoped>
.price-enroll-block {
  margin: 12px 0;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background: #ffffff;
}

.price-enroll-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
}

.price-enroll-input {
  margin-top: 0.25rem;
  display: block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
}

.price-enroll-input:focus {
  outline: none;
  box-shadow: 0 0 0 2px #3297cc;
}

.price-enroll-warning {
  margin: 8px 0 0;
  text-align: center;
  font-size: 0.8rem;
  color: #92400e;
}
</style>
